import { logEvent } from "react-migration/lib/util/logEvent";

export default function logSiteCardEvent(event: EVENTS) {
  if ("context" in event) {
    logEvent(event.name, event.context);
  } else {
    logEvent(event.name, {});
  }
}

type EVENTS =
  | SITE_CARD_ACTIVITY_TAB_EVENTS
  | SITE_CARD_ATTACHMENT_EVENTS
  | SITE_CARD_EVENTS
  | SITE_CARD_SUMMARY_TAB_EVENTS
  | SITE_CARD_COMPARABLES_TAB_EVENTS
  | SITE_CARD_TIDBIT_EVENTS
  | SITES_PIPELINE_EVENTS
  | PUBLIC_MAP_EVENTS;

type SITE_CARD_ACTIVITY_TAB_EVENTS =
  | {
      name: "Site Card Activity Tab - Comment Added";
    }
  | {
      name: "Site Card Activity Tab - Comment Updated";
    };

type SITE_CARD_ATTACHMENT_EVENTS =
  | {
      name: "Site Card Attachments Tab - File Attachment Added";
    }
  | {
      name: "Site Card Attachments Tab - File Attachment Downloaded";
    }
  | {
      name: "Site Card Attachments Tab- File Attachment Deleted";
    }
  | {
      name: "Site Card Attachments Tab - Document Attachment Added";
    }
  | {
      name: "Site Card Attachments Tab - Document Attachment Removed";
    };

type SITE_CARD_EVENTS =
  | { name: "Site Card - LenderMatch Link Clicked" }
  | { name: "Site Card - LandFund Link Clicked" }
  | { name: "Site Card - View Site In Map Mode Clicked"; context?: { source: string } }
  | { name: "Site Card - Closed" }
  | { name: "Site Card - Deleted" }
  | { name: "Site Card - Arrow Navigation Clicked"; context: { direction: "previous" | "next" } }
  | { name: "Site Card - Stage Moved" }
  | { name: "Site Card Report - Basic Report Downloaded" }
  | { name: "Site Card Report - Detailed Report Downloaded" }
  | { name: "Site Card - Tab Clicked" }
  | { name: "Site Card - Label Removed" }
  | { name: "Site Card - Label Added" }
  | { name: "Site Card - Label Created" }
  | { name: "Site Card - Label Deleted" }
  | { name: "Site Card - Label Updated" }
  | { name: "Site Card - Share Site Button Clicked" }
  | { name: "Site Card - Share Site Link Copied" };

type SITE_CARD_SUMMARY_TAB_EVENTS =
  | { name: "Site Card Summary Tab - Site Image Downloaded"; context?: { source: string } }
  | {
      name: "Site Card Summary Tab - Unit Type Updated";
      context: { type: "metric" | "imperial"; source?: string };
    }
  | { name: "Site Card Summary Tab - Planning Tab Link Clicked" }
  | { name: "Site Card Summary Tab - Ownership Map Link Clicked"; context?: { source: string } }
  | { name: "Site Card Summary Tab - Ownership Tab Link Clicked"; context?: { source: string } }
  | { name: "Site Card Summary Tab - Site Notes Updated" }
  | { name: "Site Card Summary Tab - Description Updated" }
  | { name: "Site Card Summary Tab - Zoning Link Clicked" };

type SITE_CARD_COMPARABLES_TAB_EVENTS = {
  name: "Site Card Comparables Tab - Unit Type Updated";
  context: { type: "metric" | "imperial"; source?: string };
};

type TidbitType =
  | "SiteTidbitComment"
  | "SiteTidbitPlanningRecord"
  | "SiteTidbitOwnershipRecord"
  | "SiteTidbitPricepaidRecord"
  | "SiteTidbitAttachment"
  | "SiteTidbitOSMapPrint"
  | "SiteTidbitTitleDocument";

type SITE_CARD_TIDBIT_EVENTS =
  | {
      name: "Site Card Tidbits - Tidbit Pinned";
      context: {
        type: TidbitType;
      };
    }
  | {
      name: "Site Card Tidbits - Tidbit Hidden";
      context: {
        type: TidbitType;
      };
    }
  | {
      name: "Site Card Tidbits - Tidbit Unhidden";
      context: {
        type: TidbitType;
      };
    }
  | {
      name: "Site Card Tidbits - Tidbit Deleted";
      context: {
        type: TidbitType;
      };
    }
  | {
      name: "Site Card Tidbits - Tidbit Comment Updated";
      context: {
        type: TidbitType;
      };
    };

type SITES_PIPELINE_EVENTS =
  | { name: "Sites Pipeline - Site Opened"; context: { view: "list" | "stages" } }
  | { name: "Sites Pipeline - Site Hidden" }
  | { name: "Sites Pipeline - Site Unhidden" }
  | {
      name: "Sites Pipeline - Site Priority Updated";
      context: { priority: "Low" | "Medium" | "High" };
    }
  | { name: "Sites Pipeline - Site Reassigned" }
  | { name: "Sites Pipeline - Site Dragged" }
  | { name: "Sites Pipeline - Site Deleted" }
  | { name: "Sites Pipeline - Site Share Link Copied" }
  | { name: "Sites Pipeline - Owners Exported" }
  | {
      name: "Sites Pipeline - Stage Colour Updated";
      context: { colour: string; id: string; name: string };
    }
  | { name: "Sites Pipeline - All Sites In Stage Moved" }
  | { name: "Sites Pipeline - Stage Hidden" }
  | { name: "Sites Pipeline - Stage Unhidden" }
  | { name: "Sites Pipeline - Stage Deleted" }
  | { name: "Sites Pipeline - All Sites In Stage Reassigned" }
  | { name: "Sites Pipeline - All Sites In Stage Deleted" }
  | { name: "Sites Pipeline - Stage Moved" }
  | { name: "Sites Pipeline - Stage Reordered" }
  | { name: "Sites Pipeline - Stage Renamed" }
  | { name: "Sites Pipeline - Stage Created" }
  | { name: "Sites Pipeline - Site Card Opened" }
  | { name: "Sites Pipeline - View Changed"; context: { view: "list" | "stages" } }
  | { name: "Sites Pipeline - Sites Filter Applied"; context: { filterName: string } }
  | { name: "Sites Pipeline - Sites Filter Opened"; context: { filterName: string } }
  | { name: "Sites Pipeline - Site Filters Cleared" }
  | { name: "Sites Pipeline - Sites Searched"; context: { type: "label" | "text" } }
  | { name: "Sites Pipeline - Sites Search Removed" }
  | { name: "Sites Pipeline - List Sorted"; context: { field: string } }
  | {
      name: "Sites Pipeline - Hidden Sites And Stages Visibility Toggled";
      context: { mode: "Show" | "Hide" };
    }
  | { name: "Sites Pipeline - FAQs Clicked" }
  | { name: "Sites Pipeline - Video Started"; context: { title: string } }
  | { name: "Sites Pipeline - Bulk Sites Deleted" }
  | {
      name: "Sites Pipeline - Bulk Sites Updated";
      context: { field: string; numberOfSites: number };
    }
  | { name: "Sites Pipeline - Bulk Owners Exported" };

type PUBLIC_MAP_EVENTS = { name: "Public Map - Site View Loaded" };
